/* eslint-disable react/prop-types */
/**
 * Error boundary to prevent uncaught errors from unmounting the entire tree.
 *
 * This component should be placed around major pieces of functionality to
 * limit the blast radius of an exception.
 */

import React, { Fragment, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import LegacyButton from '@atlaskit/button/button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import SectionMessage from '@atlaskit/section-message/message';
import SectionMessageAction from '@atlaskit/section-message/message-action';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Image from '@atlaskit/image/image';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';

import { getGraphqlErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';

import ErrorGraphic from '../assets/ErrorGraphic.svg';

import { DevErrorDetail } from './DevErrorDetail.tsx';
import { ErrorDetail } from './ErrorDetail.tsx';
import type { ErrorComponentProps } from './GenericErrorBoundary.tsx';

export const FULL_SIZE_ERROR_COMPONENT_BREAKPOINT = 1024;

export const i18n: {
	actionCloseModal: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	actionReloadPage: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	status403ActionReload: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	title: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	editorTitle: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	refresh: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	refreshLink: {
		id: string;
		description: string;
		defaultMessage: string;
	};
} = defineMessages({
	actionCloseModal: {
		id: 'error-boundary.close',
		description:
			'The text is used as an accessible label for a button that closes the error dialog shown when an unexpected error occurs in the application.',
		defaultMessage: 'Close',
	},
	actionReloadPage: {
		id: 'error-boundary.reload-page',
		description: 'The button/link which triggers a refresh/reload of the current page',
		defaultMessage: 'Reload page',
	},
	status403ActionReload: {
		id: 'error-boundary.status.403.action.reload',
		description:
			'The button/link which triggers a refresh/reload of the current page specifically when there is a 403 error.',
		defaultMessage: 'Reload now',
	},

	title: {
		id: 'error-boundary.title',
		description: 'Title shown when displaying a technical error',
		defaultMessage: 'Something went wrong on our end',
	},

	editorTitle: {
		id: 'error-boundary.editor.title',
		description: 'Title shown when displaying a technical error in the editor',
		defaultMessage: 'Content failed to load',
	},

	refresh: {
		id: 'error-boundary.refresh',
		description:
			"The refresh field in error boundary component. {refreshLink} will be substituted with a message that has id='error-boundary.description.refresh' which reads as 'Refresh'",
		defaultMessage: 'Try refreshing. {refreshLink}',
	},

	refreshLink: {
		id: 'error-boundary.description.refresh-link',
		description: 'The text of the link which will help user refresh the page',
		defaultMessage: 'Refresh',
	},
});

export type ErrorComponentModalProps = {
	modal?:
		| boolean
		| {
				closeText?: string;
				onClose?(): void;
		  };
};

const errorComponentContainerStyles = xcss({
	position: 'relative',
	height: '100%',

	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',

	backgroundColor: 'color.background.danger',
});

const sectionMessageContainerStyles = xcss({
	maxWidth: '600px',
});
const fullSizeErrorContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
});
const fullSizeTitleStyles = xcss({
	font: 'font.heading.large',
});

const fullSizeErrorDetailStyles = xcss({
	font: 'font.body',
});

const fullSizeErrorActionsStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: 'space.100',
});

const fullSizeErrorGraphicContainerStyles = xcss({
	alignSelf: 'center',
});

const fullSizeErrorDetailContainerStyles = xcss({
	maxWidth: '600px',
});

const devOnlyErrorDetailScrollContainerStyles = xcss({
	overflowY: 'scroll',
	maxHeight: '400px',
});

export const ErrorComponent = ({
	attribution,
	error,
	errorInfo,
	customTitle,
	modal,
}: ErrorComponentProps & ErrorComponentModalProps): React.JSX.Element => {
	const intl = useIntl();
	const [isModalOpen, setModalOpen] = useState(true);
	const [showDevErrorDetail, setShowDevErrorDetail] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [width, setWidth] = useState<number | undefined>(undefined);
	const isFullSizeErrorComponent = !!width && width >= FULL_SIZE_ERROR_COMPONENT_BREAKPOINT;
	const graphqlErrorAttributes = getGraphqlErrorAttributes(error);

	useEffect(() => {
		focusError();
	}, []);

	const focusError = () => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const error = document.querySelector(`#error-container`);
		return (error as HTMLElement)?.focus();
	};

	let title;
	if (customTitle) {
		title = customTitle;
	} else if (attribution === 'fabric') {
		title = intl.formatMessage(i18n.editorTitle);
	} else {
		title = intl.formatMessage(i18n.title);
	}

	let errorDetail = (
		<ErrorDetail {...{ attribution, error, errorInfo, isFullSizeErrorComponent }} />
	);
	const actions: [
		{
			key: string;
			text: string;
			onClick: () => any;
			appearance: 'danger' | 'subtle';
			autoFocus?: boolean;
		},
	] = [
		{
			key: 'error-boundary.reload-page',
			text:
				graphqlErrorAttributes?.statusCode === 403
					? intl.formatMessage(i18n.status403ActionReload)
					: intl.formatMessage(i18n.actionReloadPage),
			appearance: 'danger',
			autoFocus: true,
			onClick() {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'errorComponentReloadAction',
						source: 'errorComponent',
					},
				}).fire();

				window.location.reload();
			},
		},
	];

	if (process.env.NODE_ENV !== 'production') {
		actions.push({
			key: 'error-boundary.show-dev-error-details',
			appearance: 'subtle',
			text: `${showDevErrorDetail ? 'Hide' : 'Show'} dev-only details`,
			onClick() {
				setShowDevErrorDetail(!showDevErrorDetail);
			},
		});
	}
	if (showDevErrorDetail && !isFullSizeErrorComponent) {
		errorDetail = (
			<Fragment>
				{errorDetail}
				<DevErrorDetail {...{ attribution, error, errorInfo }} />
			</Fragment>
		);
	}

	if (modal) {
		const onModalClose = () => setModalOpen(false);
		const modalActions = [...actions];

		// when content failed to load in editor
		// we do not want to allow user to close the error modal
		// because it will just lead them to empty page
		if (attribution !== 'fabric') {
			modalActions.push({
				key: 'error-boundary.close-modal',
				appearance: 'subtle',
				text:
					(typeof modal === 'object' && modal.closeText) ||
					intl.formatMessage(i18n.actionCloseModal),
				onClick: (typeof modal === 'object' && modal.onClose) || onModalClose,
			});
		}

		return (
			<ModalTransition>
				{isModalOpen ? (
					<Modal onClose={onModalClose}>
						<ModalHeader hasCloseButton>
							<ModalTitle appearance="danger">{title}</ModalTitle>
						</ModalHeader>
						<ModalBody>{errorDetail}</ModalBody>
						<ModalFooter>
							{modalActions.map(({ onClick, appearance, autoFocus, key, text }) => (
								<LegacyButton
									autoFocus={autoFocus}
									appearance={appearance}
									onClick={onClick}
									key={key}
								>
									{text}
								</LegacyButton>
							))}
						</ModalFooter>
					</Modal>
				) : null}
			</ModalTransition>
		);
	}

	const sectionMessage = (
		<Box id="error-container" xcss={sectionMessageContainerStyles} tabIndex={0}>
			<SectionMessage
				appearance="error"
				actions={actions.map(({ onClick, key, text }) => (
					<SectionMessageAction onClick={onClick} key={key}>
						{text}
					</SectionMessageAction>
				))}
				title={title}
			>
				{errorDetail}
			</SectionMessage>
		</Box>
	);

	const renderResponsiveErrorComponent = () => {
		if (isFullSizeErrorComponent) {
			return (
				<Box
					id="error-container"
					padding="space.200"
					tabIndex={0}
					xcss={fullSizeErrorContainerStyles}
				>
					<Inline alignBlock="start" alignInline="start" shouldWrap={false} space="space.200">
						<Stack space="space.200" xcss={fullSizeErrorDetailContainerStyles}>
							<Box xcss={fullSizeTitleStyles}>{title}</Box>
							<Box xcss={fullSizeErrorDetailStyles}>{errorDetail}</Box>
							{showDevErrorDetail && (
								<Box xcss={devOnlyErrorDetailScrollContainerStyles}>
									<DevErrorDetail {...{ attribution, error, errorInfo }} />
								</Box>
							)}
							<Box xcss={fullSizeErrorActionsStyles}>
								{actions.map(({ onClick, key, text, appearance }) => (
									<Button onClick={onClick} key={key} appearance={appearance}>
										{text}
									</Button>
								))}
							</Box>
						</Stack>
						<Box xcss={fullSizeErrorGraphicContainerStyles}>
							<Image testId="error-graphic" src={ErrorGraphic} alt="" />
						</Box>
					</Inline>
				</Box>
			);
		}
		return sectionMessage;
	};

	return (
		<Box
			padding="space.200"
			backgroundColor="elevation.surface.overlay"
			xcss={errorComponentContainerStyles}
		>
			<Inline shouldWrap={false} space="space.200">
				{renderResponsiveErrorComponent()}
			</Inline>
			<WidthObserver setWidth={setWidth} />
		</Box>
	);
};
